import { useEffect, useState } from 'react'
import { codeToHtml } from 'shiki'
import type { ReactNode } from 'react'

interface DocsCodeBlockProps {
  children?: ReactNode
  className?: string
}

export function DocsCodeBlock({ children, className }: DocsCodeBlockProps) {
  const [html, setHtml] = useState<string>('')
  const [loading, setLoading] = useState(true)

  // Extract language from className (format: "language-javascript")
  const language = className?.replace('language-', '') || 'text'
  const code = String(children || '').replace(/\n$/, '') // Remove trailing newline

  useEffect(() => {
    let cancelled = false

    async function highlight() {
      try {
        const highlighted = await codeToHtml(code, {
          lang: language,
          themes: {
            light: 'github-light',
            dark: 'github-dark',
          },
        })

        if (!cancelled) {
          setHtml(highlighted)
          setLoading(false)
        }
      } catch (error) {
        console.warn(`Failed to highlight code (lang: ${language}):`, error)
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    highlight()

    return () => {
      cancelled = true
    }
  }, [code, language])

  // Loading state
  if (loading) {
    return (
      <div className="relative rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)] my-6">
        {language !== 'text' && (
          <div className="absolute top-3 right-3 text-xs text-[var(--color-text-tertiary)] font-mono uppercase">
            {language}
          </div>
        )}
        <pre className="p-4 overflow-x-auto">
          <code className="text-sm font-mono text-[var(--color-text-secondary)]">
            {children}
          </code>
        </pre>
      </div>
    )
  }

  // Highlighted code block
  return (
    <div className="relative group my-6">
      {language !== 'text' && (
        <div className="absolute top-3 right-3 text-xs text-[var(--color-text-tertiary)] font-mono uppercase z-10 bg-[var(--color-bg-secondary)]/80 backdrop-blur-md px-2.5 py-1 rounded-full">
          {language}
        </div>
      )}
      <div
        className="docs-code-block rounded-2xl border border-[var(--color-border)] overflow-hidden [&_pre]:!m-0 [&_pre]:!p-4 [&_pre]:!bg-transparent [&_pre]:overflow-x-auto [&_code]:text-sm [&_code]:font-mono"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <button
        onClick={() => {
          navigator.clipboard.writeText(code)
        }}
        className="absolute top-3 right-16 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-brisk active:scale-[0.97] text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] bg-[var(--color-bg-secondary)]/80 backdrop-blur-md hover:bg-[var(--color-bg-tertiary)] px-2.5 py-1 rounded-full border border-[var(--color-border)] flex items-center gap-1"
        title="Copy code"
      >
        <span className="material-symbols-outlined text-sm">content_copy</span>
        Copy
      </button>
    </div>
  )
}
